import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Grid, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { practice } from '@/api/endpoints';
import { formatPrice } from '@/utils/format';
import type { MembershipPlan } from '@/types/medical';

const PLAN_TYPES = [
  { code: 'individual',   label: 'Individual' },
  { code: 'family',       label: 'Family' },
  { code: 'employer',     label: 'Employer-sponsored' },
  { code: 'practice',     label: 'Practice-wide' },
  { code: 'chronic_care', label: 'Chronic care' },
];

export function PracticeMembershipPlansPage() {
  const q = useApiQuery({
    queryKey: ['practice', 'membership-plans'],
    queryFn:  practice.membershipPlans.list as () => Promise<MembershipPlan[]>,
  });
  const [editing, setEditing] = useState<MembershipPlan | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHeader
        title="Membership plans"
        actions={
          <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCreating(true)}>
            New plan
          </Button>
        }
      />
      <DataState query={q} empty={{ title: 'No membership plans yet' }}>
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((p) => <Row key={p.id} plan={p} onEdit={() => setEditing(p)} />)}
          </Stack>
        )}
      </DataState>

      {(creating || editing) && (
        <PlanDialog plan={editing}
                    onClose={() => { setEditing(null); setCreating(false); }} />
      )}
    </>
  );
}

function Row({ plan, onEdit }: { plan: MembershipPlan; onEdit: () => void }) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <SubscriptionsIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {plan.name}
              </Typography>
              <Chip size="small" label={plan.plan_type.replace(/_/g, ' ')}
                    variant="outlined"
                    sx={{ textTransform: 'capitalize' }} />
              {!plan.is_active && (
                <Chip size="small" label="inactive" variant="outlined" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {plan.benefits?.length ?? 0} benefit(s)
              {plan.min_commitment_months > 0 && ` · ${plan.min_commitment_months} mo minimum`}
            </Typography>
          </Box>
          <Typography variant="subtitle1" className="tabular" fontWeight={600}>
            {formatPrice(plan.monthly_price_amount, plan.price_currency)}/mo
          </Typography>
          <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>Edit</Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function PlanDialog({
  plan, onClose,
}: { plan: MembershipPlan | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = plan !== null;

  const [name, setName] = useState(plan?.name ?? '');
  const [planType, setPlanType] = useState(plan?.plan_type ?? 'individual');
  const [monthlyPrice, setMonthlyPrice] = useState(plan?.monthly_price_amount ?? '');
  const [currency, setCurrency] = useState(plan?.price_currency ?? 'EUR');
  const [minMonths, setMinMonths] = useState(String(plan?.min_commitment_months ?? 0));
  const [benefits, setBenefits] = useState((plan?.benefits ?? []).join('\n'));
  const [isActive, setIsActive] = useState(plan?.is_active ?? true);

  useEffect(() => {
    setName(plan?.name ?? '');
    setPlanType(plan?.plan_type ?? 'individual');
    setMonthlyPrice(plan?.monthly_price_amount ?? '');
    setCurrency(plan?.price_currency ?? 'EUR');
    setMinMonths(String(plan?.min_commitment_months ?? 0));
    setBenefits((plan?.benefits ?? []).join('\n'));
    setIsActive(plan?.is_active ?? true);
  }, [plan?.id]);

  const saveMut = useApiMutation({
    mutationFn: () => {
      const payload = {
        name,
        plan_type: planType,
        monthly_price_amount: monthlyPrice,
        price_currency: currency,
        min_commitment_months: parseInt(minMonths, 10) || 0,
        benefits: benefits.split('\n').map((s) => s.trim()).filter(Boolean),
        is_active: isActive,
      };
      return isEdit
        ? practice.membershipPlans.update(plan!.id, payload)
        : practice.membershipPlans.create(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Plan saved.' : 'Plan created.');
      qc.invalidateQueries({ queryKey: ['practice', 'membership-plans'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const valid = name.trim().length > 1 && parseFloat(monthlyPrice || '0') >= 0;

  return (
    <Dialog open onClose={() => !saveMut.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit plan' : 'New plan'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={8}>
            <TextField fullWidth required label="Name"
                       value={name} onChange={(e) => setName(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth select label="Type"
                       value={planType}
                       onChange={(e) => setPlanType(e.target.value as MembershipPlan['plan_type'])}>
              {PLAN_TYPES.map((t) => (
                <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField fullWidth required label="Monthly price"
                       value={monthlyPrice}
                       onChange={(e) => setMonthlyPrice(e.target.value)}
                       inputMode="decimal" />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField fullWidth select label="Currency"
                       value={currency}
                       onChange={(e) => setCurrency(e.target.value)}>
              {['EUR', 'USD', 'PLN', 'GBP'].map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Min commitment (months)" type="number"
                       value={minMonths}
                       onChange={(e) => setMinMonths(e.target.value)}
                       inputProps={{ min: 0, max: 36 }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={4} label="Benefits"
                       helperText="One benefit per line."
                       value={benefits}
                       onChange={(e) => setBenefits(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={isActive}
                               onChange={(_, v) => setIsActive(v)} />}
              label="Active (purchasable)" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveMut.isPending}>Cancel</Button>
        <Button variant="contained"
                disabled={!valid || saveMut.isPending}
                onClick={() => saveMut.mutate()}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
